import React, { ReactNode, useEffect, useRef } from 'react';
import {  Box, Stack, Typography, ToggleButtonGroup, ToggleButton, styled, Select, SelectChangeEvent, MenuItem, Checkbox } from '@mui/material';

import AthleteSearch from './AthleteSearch';
import { getAthleteDetails } from '../api/resultsClient';
import { AthleteResult } from '../domain/AthleteDetail';
import { useTheme } from '@mui/material/styles';

import { BookmarkBorder, Bookmark, ReportProblem } from '@mui/icons-material';
import { AthleteSearchResult } from '../domain/AthleteSearchResult';
import StandardsTable from '../standards/StandardsTable';
import { StandardRow } from '../domain/Standard';

const getStandard = async (name: string, gender: string, course: string): Promise<StandardRow[]> => {
    const standardFilename = `${name}-${gender}-${course}.json`;
    return (await import(`../data/${standardFilename}`)).default as StandardRow[];
}

const searchDisabled = true;

const standardNames = [
    "CoupeQuebecJunior",
    "ChampionnatProvincial",
    "CoupeQuebecSenior",
    "GrandPrixQuebec",
    "CanadaTrials",
    "CanadaOpen",
    "OJI",
    "OJIBonus",
    "ChampionnatProvincialBonus",
];

const standardLastUpdates: Record<string, string> = {
    CoupeQuebecJunior: "8 septembre 2026",
    ChampionnatProvincial: "8 septembre 2026",
    CoupeQuebecSenior: "8 septembre 2026",
    ChampionnatProvincialBonus: "8 septembre 2026",
    OJI: "juin 2026",
    OJIBonus: "juin 2026",
    CanadaTrials: "23 janvier 2026",
    CanadaOpen: "23 janvier 2026",
};

function AthleteSearchPage() {
    const theme = useTheme();
    const [genderLocked, setGenderLocked] = React.useState(false);
    const [courseLocked, setCourseLocked] = React.useState(false);
    const [standardOptions, setStandardOptions] = React.useState({ athleteGender: 'H', course: '25m' });
    const [standard, setStandard] = React.useState<StandardRow[]>([]);

    const [isFavorite, setIsFavorite] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const [selectedStandardIndex, setSelectedStandardIndex] = React.useState(0);
    const [currentAthlete, setCurrentAthlete] = React.useState<AthleteSearchResult | null>(null);
    const [athleteResults, setAthleteResults] = React.useState<AthleteResult[]>([]);

    let storedFavoriteList = localStorage.getItem('favorites');

    const [favoriteList, setFavoriteList] = React.useState<AthleteSearchResult[]>(JSON.parse(storedFavoriteList ? storedFavoriteList : '[]'));

    // Ref for the standard select
    const standardSelectRef = useRef<HTMLSelectElement>(null);

    const isCurrentStandardIsLCOnly = (standardIndexValue: number): boolean => {
        return false;
    }

    const handleChange = (event: SelectChangeEvent<number>, node: ReactNode) => {
        const standardIndexValue = event.target.value as number;
        setSelectedStandardIndex(standardIndexValue);

        if (isCurrentStandardIsLCOnly(standardIndexValue))
        {
            setStandardOptions({...standardOptions, course: '50m'});
            setCourseLocked(true);
        }
        else {
            setCourseLocked(false);
        }
    };

    const handleGender = (
        event: React.MouseEvent<HTMLElement>,
        gender: string | null,
      ) => {
        if (gender !== null) {
            setStandardOptions({...standardOptions, athleteGender: gender});
        }
      };

      const handleCourse = (
        event: React.MouseEvent<HTMLElement>,
        course: string | null,
      ) => {
        if (course !== null) {
            setStandardOptions({...standardOptions, course});
        }
      };

      const handleIsFavoriteChange = (
        event: React.ChangeEvent<HTMLElement>,
        checked: boolean | null,
      ) => {
        if (checked && currentAthlete) {            

            setIsFavorite(true);

            // add athlete to favorites in local storage
             
            const favorites = localStorage.getItem('favorites');
            if (favorites) {
                const favoriteList = JSON.parse(favorites) as AthleteSearchResult[];
                
                if (favoriteList.map(item => item.id).indexOf(currentAthlete.id) === -1) {
                    favoriteList.push(currentAthlete);
                    localStorage.setItem('favorites', JSON.stringify(favoriteList));
                    setFavoriteList(favoriteList); 
                }
            }
            else {
                localStorage.setItem('favorites', JSON.stringify([currentAthlete]));
                setFavoriteList([currentAthlete]); 
            }
                       
        }
        else {
            setIsFavorite(false);
            
            const favorites = localStorage.getItem('favorites');
            if (favorites && currentAthlete) {
                const favoriteList = JSON.parse(favorites) as AthleteSearchResult[];
                const index = favoriteList.map(item => item.id).indexOf(currentAthlete.id);
                if (index > -1) {
                    favoriteList.splice(index, 1);
                    localStorage.setItem('favorites', JSON.stringify(favoriteList));
                    setFavoriteList(favoriteList);
                }
            }
        }
      };

    useEffect(() => {
        const fetchAthleteResult = async () => {
            if (currentAthlete) {
                try {
                    setLoading(true);
                    setAthleteResults([]);
                    const favorites = localStorage.getItem('favorites');
                    
                    if (favorites) {
                        const favoriteList = JSON.parse(favorites) as AthleteSearchResult[];
                        setIsFavorite(favorites ? favoriteList.map(item => item.id).indexOf(currentAthlete.id) > -1 : false);
                    }
                    setGenderLocked(true);

                    const athleteDetails = await getAthleteDetails(currentAthlete.id);

                    if (standardOptions.athleteGender !== athleteDetails.gender) {
                        setStandardOptions({ ...standardOptions, athleteGender: athleteDetails.gender });
                    }

                    setAthleteResults(athleteDetails.results);
                }
                finally {
                    setLoading(false);
                }
            }
            else { 
                setGenderLocked(false);
                setIsFavorite(false);
                setAthleteResults([]);
            }
        }

        fetchAthleteResult();
        // Focus the standard select when athlete changes and an athlete is selected
        if (currentAthlete && standardSelectRef.current) {
            standardSelectRef.current.focus();
        }
    }, [currentAthlete, standardOptions]);

    useEffect(() => {
        async function fetchStandard() {
            setStandard(await getStandard(standardNames[selectedStandardIndex], standardOptions.athleteGender, standardOptions.course));
        }
        fetchStandard();
    }, [selectedStandardIndex, standardOptions]);

    
    const ColoredToggleButton = styled(ToggleButton)(() => ({
        "&.Mui-selected, &.Mui-selected:hover": {
          color: "black",
          backgroundColor: theme.palette.secondary.main,
        },
        "&": {
            width: '45px',
            height: '45px'
        }
      }));

    return (
        <form>
            <Box display='flex' flexWrap={'wrap'} sx={{ m: 1, mb: 2 }}>
                { !searchDisabled &&
                    <Box sx={{ minWidth: '390px', alignSelf: 'flex-end', paddingRight: 2 }}>
                        <Stack direction="row" spacing={0}>
                            <AthleteSearch updateCurrentAthlete={setCurrentAthlete} favoriteList={favoriteList} />
                            <Checkbox
                                checked={isFavorite}
                                onChange={handleIsFavoriteChange}
                                icon={<BookmarkBorder />}
                                checkedIcon={<Bookmark />}
                                />                        
                        </Stack>                    
                    </Box>
                }      
                { searchDisabled && 
                    <Box width="100%" mb={2}>
                        <Box display="flex" alignItems="center" sx={{
                            backgroundColor: '#e3f2fd', // light blue
                            borderRadius: 2,
                            p: 2,
                        }}>
                            <ReportProblem sx={{ color: theme.palette.info.main, mr: 1, fontSize: 24 }} />
                            <Typography variant="caption" sx={{ color: theme.palette.info.dark, fontWeight: 'bold' }}>
                                Des changements apportés au site fournissant les données ont forcé la désactivation de la recherche d'athlète.
                            </Typography>
                        </Box>
                    </Box>
                }

                <Stack direction="column" spacing={1} alignItems="center" sx={{ paddingRight: 2, paddingTop: 1 }}>
                    <Typography>Genre</Typography>

                    <Stack direction="row" spacing={1} >
                        <ToggleButtonGroup
                            value={standardOptions.athleteGender}
                            exclusive
                            onChange={handleGender}
                            aria-label="text alignment"
                            disabled={genderLocked}
                            sx={{ height: '45px', color: 'primary' }}
                        >
                            <ColoredToggleButton value="H" aria-label="left aligned">
                                <Typography>H</Typography>
                            </ColoredToggleButton>
                            <ColoredToggleButton value="F" aria-label="centered">
                                <Typography>F</Typography>
                            </ColoredToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Stack>
            
                <Stack direction="column" spacing={1} sx={{ paddingRight: 2, paddingTop: 1 }}>
                    <Typography>Bassin</Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <ToggleButtonGroup
                            value={standardOptions.course}
                            exclusive
                            onChange={handleCourse}
                            disabled={courseLocked}
                            aria-label="text alignment"
                            sx={{ height: '35px' }}
                        >
                            <ColoredToggleButton value="25m" aria-label="left aligned">
                                <Typography>25m</Typography>
                            </ColoredToggleButton>
                            <ColoredToggleButton value="50m" aria-label="centered">
                                <Typography>50m</Typography>
                            </ColoredToggleButton>
                        </ToggleButtonGroup>
                    </Stack>                    
                </Stack>
            </Box>

            <Box display='flex' flexWrap={'wrap'} sx={{ m:1 }}>
                <Stack direction="column" spacing={2} width="100%">
                    
                    <Box sx={{ textAlign: 'left'}}>
                        <Select
                            value={selectedStandardIndex}
                            onChange={handleChange}
                            sx={{ width: '300px'}}
                            inputRef={standardSelectRef}
                        >
                            <MenuItem value={0}>Coupe Espoir Québec</MenuItem>
                            <MenuItem value={1}>Championnat Québécois</MenuItem>
                            <MenuItem value={8}>Championnat Québécois Bonus</MenuItem>
                            <MenuItem value={2}>Coupe du Québec</MenuItem>
                            <MenuItem value={3}>Grand Prix du Québec</MenuItem>
                            <MenuItem value={4}>Essais canadiens</MenuItem>
                            <MenuItem value={5}>Omnium canadien</MenuItem>
                            <MenuItem value={6}>Ontario Junior International</MenuItem>
                            <MenuItem value={7}>Ontario Junior International Bonus</MenuItem>
                        </Select>
                    </Box>                    
                
                    {React.useMemo(
                        () => <StandardsTable loading={loading} standards={standard} athleteResults={athleteResults} options={standardOptions} />,
                        [loading, standard, athleteResults, standardOptions]
                    )}
                    {standardLastUpdates[standardNames[selectedStandardIndex]] &&
                        <Typography variant="caption" sx={{ textAlign: 'left' }}>
                            Dernière mise à jour : {standardLastUpdates[standardNames[selectedStandardIndex]]}
                        </Typography>
                    }
                </Stack>                
            </Box>            
        </form>
    );
}

export default AthleteSearchPage;
